import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import StopWeightSections, { weightSections } from './ShipmentWeightBreakdown';
import { currentVesselName } from '../../../utils/hubWait';
import { formatCarrierDelay, delayBarColor } from '../../../utils/shipmentCardDisplay';

const DASH = '—';

/** Lazy body: only mounted while its stop is expanded, so weights are computed on demand. */
const StopBody = ({ stop, stopId, isDark }) => {
  const sections = useMemo(() => weightSections(stop), [stop]);
  const [closed, setClosed] = useState({});
  const hasRows = sections.some((s) => s.groups.length > 0);
  if (!hasRows) {
    return <div className={`px-2 py-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>{DASH}</div>;
  }
  return (
    <StopWeightSections
      sections={sections}
      stopId={stopId}
      isDark={isDark}
      pluralize
      commodityIsOpen={(key) => closed[key] !== true}
      toggleCommodity={(key) => setClosed((prev) => ({ ...prev, [key]: prev[key] !== true }))}
    />
  );
};

const StopRow = ({ row, expanded, onToggle, isDark, t }) => {
  const muted = isDark ? 'text-gray-400' : 'text-[#4b5563]';
  const pct = row.progress;
  const fill = pct == null ? 0 : Math.min(Math.max(pct, 0), 100);
  const delayVal = row.delay_days;
  const late = Number(delayVal) >= 1;
  const delayLabel = delayVal == null || delayVal === '' ? null : formatCarrierDelay(delayVal);
  const head = (
    <>
      <span className="flex justify-between items-center gap-2">
        <span className="inline-flex items-center gap-1 min-w-0">
          {!row.fromCard && (
            <ChevronDown size={12} className={`shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} />
          )}
          <span className="whitespace-normal break-words">{row.destination || DASH}</span>
        </span>
        <span className="shrink-0">
          {row.boxes == null ? DASH : t('seaTracking.card.boxesCount', { count: row.boxes })}
        </span>
      </span>
      {row.merged ? (
        <span className={`mt-1 flex justify-between gap-2 text-[0.625rem] font-normal ${muted}`}>
          <span>
            {`${t('seaTracking.card.shipmentsCount', { count: row.shipments })} · ${t('seaTracking.card.originEtd')} ${row.etdFromLabel || DASH}${row.etdToLabel && (row.etdToLabel !== row.etdFromLabel || row.etdToLabel === DASH) ? ` – ${row.etdToLabel}` : ''}`}
          </span>
          <span className="text-right whitespace-nowrap">{`${t('seaTracking.card.latestEta')} ${row.latestEtaLabel || DASH}`}</span>
        </span>
      ) : (
        <>
      <span className="relative mt-1 flex w-full items-center">
        <span className={`block w-full h-3 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <span
            className="block h-3 rounded-full"
            style={{ width: `${fill}%`, backgroundColor: delayBarColor(delayVal) }}
          />
        </span>
        <span
          className={`flex absolute inset-0 items-center justify-center pointer-events-none font-semibold text-xs ${late ? 'text-[#7f1d1d]' : (isDark ? 'text-gray-100' : 'text-[#14532d]')}`}
        >
          {pct == null ? DASH : `${pct}%`}
        </span>
      </span>
      <span className={`mt-0.5 flex justify-between gap-2 text-[0.625rem] font-normal ${muted}`}>
        <span>{t('seaTracking.card.originEtd')} {row.etdLabel || DASH}</span>
        <span className="text-right whitespace-nowrap">
          {t('seaTracking.card.stopEta')} {row.etaLabel || DASH}
          {delayLabel && (
            <>
              {' · '}
              <span className={late ? 'text-red-600 dark:text-red-400' : ''}>{delayLabel}</span>
            </>
          )}
        </span>
      </span>
        </>
      )}
    </>
  );
  const cls = `w-full px-2 py-1.5 text-xs font-bold text-left border ${expanded ? 'rounded-t' : 'rounded'} ${
    isDark ? 'bg-[#262626] border-[#333] text-white' : 'bg-white border-[#e5e7eb] text-[#1f2937]'
  }`;
  const hover = row.fromCard ? '' : (isDark ? ' hover:bg-[#2f2f2f]' : ' hover:bg-gray-50');
  return (
    <div className="mt-1.5">
      {row.fromCard ? (
        <div className={cls}>{head}</div>
      ) : (
        <button type="button" aria-expanded={expanded} onClick={onToggle} className={`${cls}${hover}`}>{head}</button>
      )}
      {expanded && !row.fromCard && <StopBody stop={row.stop} stopId={row.key} isDark={isDark} />}
    </div>
  );
};

/**
 * "Waiting at {port}": one panel for a hub pin. Each trip (next vessel + leaving date)
 * lists the stops that travel together; stop bodies mount only when expanded.
 * `onSelectShipment` opens the normal card for an in-transit (non-hub) row.
 */
const HubWaitingPanel = ({
  panel, focusTripKey = null, focusSeq = 0, isDark = false, onClose, onSelectShipment, onSelectHub, defaultOpenKeys = [],
}) => {
  const { t } = useTranslation();
  const focusStops = () => (panel?.trips.find((x) => x.key === focusTripKey)?.stops || []).map((r) => r.key);
  const [open, setOpen] = useState(() => Object.fromEntries([...defaultOpenKeys, ...focusStops()].map((k) => [k, true])));
  const focusRef = useRef(null);
  // A search that focuses a trip expands its stops; ordinary pin clicks leave them collapsed.
  useEffect(() => {
    if (!focusTripKey) return;
    setOpen((prev) => ({ ...prev, ...Object.fromEntries(focusStops().map((k) => [k, true])) }));
    focusRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [focusTripKey, focusSeq]);
  if (!panel || panel.tripCount === 0) return null;

  const sub = isDark ? 'text-gray-400' : 'text-[#6b7280]';
  const border = isDark ? 'border-[#333]' : 'border-[#e5e7eb]';
  const TBC = t('seaTracking.card.toBeConfirmed');
  return (
    <div
      className={`pointer-events-auto relative z-0 w-[340px] max-w-full min-h-0 shrink max-h-[560px] overflow-y-auto rounded-lg shadow-lg border text-xs ${
        isDark ? 'bg-[#1d1d1d] border-[#333] text-gray-200' : 'bg-white border-[#e5e7eb] text-[#1f2937]'
      }`}
    >
      <div className={`px-2.5 py-1.5 font-semibold border-b flex items-center justify-between gap-2 ${border}`}>
        <span className="min-w-0">
          {t('seaTracking.card.hubWaitingAt', { port: panel.port })}
          <span className={`block font-normal ${sub}`}>
            {t('seaTracking.card.hubSummaryTrips', {
              boxes: t('seaTracking.card.boxesCount', { count: panel.totalBoxes }),
              trips: t('seaTracking.card.tripsCount', { count: panel.tripCount }),
            })}
            {panel.earliestDepartureLabel && t('seaTracking.card.hubSummaryEarliest', { date: panel.earliestDepartureLabel })}
            {panel.overdueCount > 0 && t('seaTracking.card.hubSummaryOverdue', { count: panel.overdueCount })}
          </span>
        </span>
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={onClose}
          className={`shrink-0 text-base leading-none px-1 rounded ${isDark ? 'text-gray-300 hover:bg-[#333]' : 'text-gray-500 hover:bg-gray-100'}`}
        >
          ×
        </button>
      </div>
      {panel.trips.map((trip) => (
        <section
          key={trip.key}
          ref={trip.key === focusTripKey ? focusRef : null}
          className={`px-2.5 py-2 border-b ${border} ${trip.key === focusTripKey ? (isDark ? 'bg-[#282a29]' : 'bg-gray-50') : ''}`}
        >
          <div className="font-semibold">
            {t('seaTracking.card.hubTripNext', {
              vessel: trip.vessel || TBC,
              date: trip.departureLabel || TBC,
              boxes: t('seaTracking.card.boxesCount', { count: trip.boxes }),
            })}
          </div>
          {trip.overdue && (
            <div className={`font-semibold ${isDark ? 'text-amber-400' : 'text-[#B45309]'}`}>{t('seaTracking.card.hubOverdueMarker')}</div>
          )}
          {trip.stops.map((row) => (
            <StopRow
              key={row.key}
              row={row}
              expanded={Boolean(open[row.key])}
              onToggle={() => setOpen((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
              isDark={isDark}
              t={t}
            />
          ))}
        </section>
      ))}
      {panel.otherHubs.length > 0 && (
        <>
          <div className={`px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide ${isDark ? 'bg-[#282a29] text-gray-400' : 'bg-[#f3f4f6] text-[#6b7280]'}`}>
            {t('seaTracking.card.otherPortsWaiting')}
          </div>
          {panel.otherHubs.map((h) => (
            <button
              key={h.hubKey}
              type="button"
              onClick={() => onSelectHub(h)}
              className={`w-full text-left px-2.5 py-1.5 ${isDark ? 'hover:bg-[#3a3a3a]' : 'hover:bg-gray-200'}`}
            >
              <div className="font-semibold truncate">{t('seaTracking.card.hubWaitingAt', { port: h.port })}</div>
              <div className={sub}>{t('seaTracking.card.boxesCount', { count: h.boxes })}</div>
            </button>
          ))}
        </>
      )}
      {panel.others.length > 0 && (
        <>
          <div className={`px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide ${isDark ? 'bg-[#282a29] text-gray-400' : 'bg-[#f3f4f6] text-[#6b7280]'}`}>
            {t('seaTracking.card.inTransit')}
          </div>
          {panel.others.map((s) => {
            const boxes = Number(s.total_containers);
            return (
              <button
                key={s.container_number}
                type="button"
                onClick={() => onSelectShipment(s)}
                className={`w-full text-left px-2.5 py-1.5 ${isDark ? 'hover:bg-[#3a3a3a]' : 'hover:bg-gray-200'}`}
              >
                <div className="font-semibold truncate">{currentVesselName(s) || s.container_number}</div>
                <div className={sub}>
                  {s.arrival_port || DASH}
                  {Number.isFinite(boxes) && boxes > 0 ? ` · ${t('seaTracking.card.boxesCount', { count: boxes })}` : ''}
                </div>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
};

export default HubWaitingPanel;
